import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Separator } from "./ui/separator";
import { services as mockServices, contactInfo } from "../data/mock";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  MapPin, Phone, Mail, Clock,
  Facebook, Instagram, Linkedin, Twitter,
  ArrowRight, Send
} from "lucide-react";

export const Footer = () => {
  const [email, setEmail] = useState("");

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!email) return;
    try {
      const result = await api.subscribeNewsletter(email);
      if (result.already_subscribed) {
        toast.info("You're already subscribed!");
      } else {
        toast.success("Thank you for subscribing!", {
          description: "You'll receive the latest immigration news and updates."
        });
      }
      setEmail("");
    } catch (err) {
      toast.error("Failed to subscribe. Please try again.");
    }
  };

  const quickLinks = [
    { path: "/", label: "Home" },
    { path: "/about", label: "About Us" },
    { path: "/blog", label: "Blog" },
    { path: "/contact", label: "Contact Us" }
  ];

  return (
    <footer className="bg-brand-950 text-white">
      {/* Newsletter Section */}
      <div className="border-b border-brand-800/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="text-xl font-bold mb-1">Stay Updated</h3>
              <p className="text-brand-300 text-sm">Get the latest immigration news and tips delivered to your inbox.</p>
            </div>
            <form onSubmit={handleNewsletter} className="flex gap-2 w-full md:w-auto">
              <Input
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="bg-brand-800/50 border-brand-700 text-white placeholder:text-brand-400 rounded-xl h-11 w-full md:w-72 focus:border-brand-400 focus:ring-brand-400"
              />
              <Button type="submit" className="bg-white text-brand-800 hover:bg-brand-100 rounded-xl h-11 px-5 font-semibold transition-all duration-300 hover:-translate-y-0.5 flex-shrink-0">
                <Send className="w-4 h-4 mr-2" />
                Subscribe
              </Button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
          {/* Brand */}
          <div>
            <div className="flex items-center gap-3 mb-5">
              <img src="/nexfly-logo.png" alt="NexFLY" className="h-14 w-auto brightness-0 invert" />
            </div>
            <p className="text-brand-300 text-sm leading-relaxed mb-6">
              Your trusted partner for immigration, study abroad, and language coaching services. Making global dreams accessible since 2022.
            </p>
            <div className="flex items-center gap-3">
              <a href={contactInfo.social.facebook} target="_blank" rel="noreferrer" className="w-9 h-9 bg-brand-800/50 rounded-lg flex items-center justify-center hover:bg-brand-500 transition-colors duration-200">
                <Facebook className="w-4 h-4" />
              </a>
              <a href={contactInfo.social.instagram} target="_blank" rel="noreferrer" className="w-9 h-9 bg-brand-800/50 rounded-lg flex items-center justify-center hover:bg-brand-500 transition-colors duration-200">
                <Instagram className="w-4 h-4" />
              </a>
              <a href={contactInfo.social.linkedin} target="_blank" rel="noreferrer" className="w-9 h-9 bg-brand-800/50 rounded-lg flex items-center justify-center hover:bg-brand-500 transition-colors duration-200">
                <Linkedin className="w-4 h-4" />
              </a>
              <a href={contactInfo.social.twitter} target="_blank" rel="noreferrer" className="w-9 h-9 bg-brand-800/50 rounded-lg flex items-center justify-center hover:bg-brand-500 transition-colors duration-200">
                <Twitter className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-semibold text-base mb-5">Quick Links</h4>
            <ul className="space-y-3">
              {quickLinks.map((link) => (
                <li key={link.path}>
                  <Link to={link.path} className="text-brand-300 hover:text-white text-sm flex items-center gap-2 group transition-colors duration-200">
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-200" />
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h4 className="font-semibold text-base mb-5">Our Services</h4>
            <ul className="space-y-3">
              {mockServices.slice(0, 6).map((service) => (
                <li key={service.slug}>
                  <Link to={`/services/${service.slug}`} className="text-brand-300 hover:text-white text-sm flex items-center gap-2 group transition-colors duration-200">
                    <ArrowRight className="w-3.5 h-3.5 group-hover:translate-x-1 transition-transform duration-200" />
                    {service.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h4 className="font-semibold text-base mb-5">Contact Info</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-brand-400 mt-0.5 flex-shrink-0" />
                <span className="text-brand-300 text-sm">{contactInfo.address}</span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-4 h-4 text-brand-400 flex-shrink-0" />
                <a href={`tel:${contactInfo.phone}`} className="text-brand-300 hover:text-white text-sm transition-colors">{contactInfo.phone}</a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-brand-400 flex-shrink-0" />
                <a href={`mailto:${contactInfo.email}`} className="text-brand-300 hover:text-white text-sm transition-colors">{contactInfo.email}</a>
              </li>
              <li className="flex items-center gap-3">
                <Clock className="w-4 h-4 text-brand-400 flex-shrink-0" />
                <span className="text-brand-300 text-sm">{contactInfo.hours}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <Separator className="bg-brand-800/50" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-brand-400 text-sm">&copy; {new Date().getFullYear()} NexFLY Immigration Consultancy. All rights reserved.</p>
          <div className="flex items-center gap-6">
            <span className="text-brand-400 text-sm hover:text-brand-300 cursor-pointer transition-colors">Privacy Policy</span>
            <span className="text-brand-400 text-sm hover:text-brand-300 cursor-pointer transition-colors">Terms of Service</span>
          </div>
        </div>
      </div>
    </footer>
  );
};
